import { StandardEvents } from '@shopify/events';

/**
 * ECS (GLOSS-63): the quick-add size bar opens from a bag-icon disclosure
 * (a native <details>). Per design QA, moving the pointer off the card
 * resets the disclosure to its initial state, so returning to the card
 * shows the bag icon again rather than the still-open bar.
 *
 * Fine pointers only: on touch there is no meaningful "pointer leaves the
 * card" moment, and the verified mobile behaviour (bar stays once opened)
 * is kept as is.
 *
 * The `toggle` event does not bubble, but a capture-phase listener on the
 * document still observes it, so one listener covers every card —
 * including cards morphed in later by filtering or infinite scroll.
 */
const FINE_POINTER = window.matchMedia('(any-pointer: fine)');

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.classList.contains('quick-add__size-toggle')) return;
    if (!details.open || !FINE_POINTER.matches) return;

    const card = details.closest('product-card, .card-gallery');
    if (!card) return;

    card.addEventListener(
      'pointerleave',
      () => {
        details.open = false;
      },
      { once: true }
    );
  },
  true
);

/**
 * ECS (GLOSS-72): adding from the bar closes it again, on EVERY pointer type.
 *
 * The pointerleave reset above only runs for fine pointers, so on touch an
 * opened bar stayed over the card indefinitely — QA saw the CTAs stuck in
 * view after an add, with no way back to the bag icon short of leaving the
 * page. Closing the disclosure returns the card to its initial state and
 * leaves it re-openable, which is what the design asks for.
 *
 * This rides the theme's own cart event rather than the button's click: the
 * bar's size buttons are real `product-form-component` submits, and that
 * component announces every add as a bubbling `CartLinesUpdateEvent`. The
 * event is dispatched optimistically when the request starts and carries a
 * promise that settles with the outcome, so the close waits for the add to
 * actually succeed — a failed add leaves the bar open, with its error where
 * the shopper can act on it.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;
  if (!(event.target instanceof Element)) return;

  // Only an add that started inside an open size bar closes one.
  const sizeForm = event.target.closest('.quick-add__size-form');
  const details = sizeForm?.closest('quick-add-component')?.querySelector('details.quick-add__size-toggle');
  if (!(details instanceof HTMLDetailsElement) || !details.open) return;

  const close = () => {
    details.open = false;
  };

  if (!event.promise) {
    close();
    return;
  }

  event.promise
    .then(({ detail }) => {
      if (!detail?.didError) close();
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[quick-add-size-toggle-reset] Event promise rejected:', error);
    });
});
